// ── Service de MKT (núcleo/aplicação) ─────────────────────────────
// Orquestra a notificação de conversões: lê o lead (via porta), monta o
// corpo do evento e delega o disparo HTTP (via gateway). Não conhece
// Supabase nem fetch. Fire-and-forget e non-fatal: NUNCA rejeita.

import type { IMktLeadRepository, LeadFull, LeadResponsavel, MktLeadEntry } from '../ports/IMktLeadRepository';
import type { IMktWebhookGateway, MktAction } from '../ports/IMktWebhookGateway';

export type { MktAction } from '../ports/IMktWebhookGateway';

// ── Erro de permissão das entradas de MKT ─────────────────────────
// A RPC levanta exceção com esta mensagem quando o auth.uid() não
// pertence ao workspace do lead. Traduzimos para uma mensagem de
// permissão; a UI distingue pelo `code` (nunca mostra o texto cru do DB).
const RPC_ACCESS_DENIED = 'Sem acesso ao workspace do lead';

export const MKT_ACCESS_DENIED_CODE = 'mkt_access_denied';

export interface MktAccessDeniedError extends Error {
  code?: string;
}

export const isMktAccessDenied = (err: unknown): boolean =>
  (err as MktAccessDeniedError | null)?.code === MKT_ACCESS_DENIED_CODE;

interface Responsaveis {
  sdr: LeadResponsavel | null;
  closer: LeadResponsavel | null;
}

// Monta o bloco { sdr, closer } a partir dos joins de profiles do lead.
const buildResponsaveis = (lead: LeadFull | null): Responsaveis => ({
  sdr: lead?.sdr ? { id: lead.sdr.id || null, nome: lead.sdr.nome || null } : null,
  closer: lead?.closer ? { id: lead.closer.id || null, nome: lead.closer.nome || null } : null,
});

export interface NotifyMktConversionOpts {
  leadId: string;
  meeting?: Record<string, unknown> | null;
  sale?: Record<string, unknown> | null;
  loss?: Record<string, unknown> | null;
  stageChange?: Record<string, unknown> | null;
  extra?: Record<string, unknown> | null;
}

export interface MktServiceDeps {
  leadRepo: IMktLeadRepository;
  webhook: IMktWebhookGateway;
}

export class MktService {
  constructor(private readonly deps: MktServiceDeps) {}

  /**
   * Entradas de marketing (origem/atribuição) do lead, da mais recente para
   * a mais antiga — as SEM data vão para o fim. A RPC ordena por
   * `entered_at desc`, e o Postgres coloca NULL primeiro nessa ordenação;
   * aqui reposicionamos para que uma captura sem data não pareça a mais
   * recente. Lista vazia é o caso normal (a maioria dos leads não tem
   * origem registrada) — não é erro.
   */
  listLeadMktEntries = async (leadId: string): Promise<MktLeadEntry[]> => {
    if (!leadId) return [];
    let rows: MktLeadEntry[];
    try {
      rows = await this.deps.leadRepo.fetchLeadMktEntries(leadId);
    } catch (err) {
      const raw = (err as Error)?.message || '';
      if (raw.includes(RPC_ACCESS_DENIED)) {
        const denied: MktAccessDeniedError = new Error(
          'Você não tem permissão para ver a origem de marketing deste lead.',
        );
        denied.code = MKT_ACCESS_DENIED_CODE;
        throw denied;
      }
      throw err;
    }
    // Estável: preserva o DESC da RPC entre as datadas e entre as sem data.
    return [...rows.filter(e => !!e.entered_at), ...rows.filter(e => !e.entered_at)];
  };

  /**
   * Notifica o n8n de uma conversão / evento comercial. Nunca rejeita.
   */
  notifyMktConversion = async (
    action: MktAction,
    { leadId, meeting = null, sale = null, loss = null, stageChange = null, extra = null }: NotifyMktConversionOpts = {} as NotifyMktConversionOpts,
  ): Promise<void> => {
    try {
      if (!action || !leadId) return;

      const lead = await this.deps.leadRepo.fetchLeadFull(leadId);

        const body = {
          action,
          occurred_at: new Date().toISOString(),
          workspace_id: lead?.workspace_id || null,
          lead_id: leadId,
          // Contato — promovido ao topo para o n8n ler direto.
          nome: lead?.nome || '',
          email: lead?.email || '',
          telefone: lead?.telefone || '',
          // Origem / canal.
          canal: lead?.canal || '',
          origem: lead?.origem || '',
          sub_origem: lead?.sub_origem || '',
          // Marketing.
          utm_source: lead?.utm_source || '',
          utm_medium: lead?.utm_medium || '',
          utm_campaign: lead?.utm_campaign || '',
          utm_content: lead?.utm_content || '',
          campanha: lead?.campanha || '',
          publico: lead?.publico || '',
          criativo: lead?.criativo || '',
          // Responsáveis (SDR / Closer).
          responsaveis: buildResponsaveis(lead),
          // Lead completo.
          lead: lead || { id: leadId },
          // Blocos específicos por ação.
          ...(stageChange ? { stage_change: stageChange } : {}),
          ...(meeting ? { meeting } : {}),
          ...(loss ? { loss } : {}),
          ...(sale ? { sale } : {}),
          ...(extra || {}),
        };

        await this.deps.webhook.sendConversion(action, body);
      } catch (err) {
        console.warn(`[mktWebhook] Falha ao notificar "${action}":`, (err as Error)?.message || err);
      }
  };
}
